import {
	Component,
	Input,
	ViewEncapsulation,
	forwardRef,
	Output,
	EventEmitter,
	AfterContentInit,
	OnDestroy,
	SimpleChanges
} from '@angular/core';
import { IdService, IGenericSelectOption, FormControlBase } from '../../shared/index';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
	selector: 'rbc-select',
	templateUrl: './select.component.html',
	styleUrls: ['./select.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => SelectComponent)
		}
	]
})
export class SelectComponent extends FormControlBase<string> implements AfterContentInit, OnDestroy {
	@Input() disabled: string | boolean = false;
	@Input() helpText: string;
	@Input() id: string;
	@Input() label: string;
	@Input() name: string;
	@Input() ariaLabel: string;
	@Input()
	get options(): IGenericSelectOption[] {
		return this._options;
	}
	set options(val: IGenericSelectOption[]) {
		this._options = val || [];
	}
	@Input() model: string;
	@Input() preselect: string;
	@Input() size: 'large' | 'small' = 'small';
	@Input() sizeWidth: string;

	@Output() selectUpdate = new EventEmitter<IGenericSelectOption>();
	@Output() modelChange = new EventEmitter<string>();

	private _options: IGenericSelectOption[] = [];

	constructor(private idService: IdService) {
		super();
		this.id = idService.generate();
	}

	ngAfterContentInit() {
		if (this.preselect) {
			this.writeValue(this.preselect);
			this.options = this.getOptionsUpdated(this.innerValue);
		} else if (this.options && this.options.length) {
			let currentSelected = this.options.find(option => option.selected === true);

			currentSelected = currentSelected ? currentSelected : this.options[0] ? this.options[0] : null;
			this.writeValue(currentSelected.value);
			this.options = this.getOptionsUpdated(this.innerValue);
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.preselect) {
			this.writeValue(changes.preselect.currentValue);
			this.options = this.getOptionsUpdated(this.innerValue);
		}

		if (changes && changes.model) {
			this.writeValue(changes.model.currentValue);
			this.options = this.getOptionsUpdated(this.innerValue);
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
		this.idService.remove(this.id);
	}

	// Handlers
	selectChangeHandler(selectEvent: Event) {
		this.setModel((selectEvent.target as HTMLInputElement).value);
		this.options = this.getOptionsUpdated(this.innerValue);
		this.selectUpdate.emit(this.options.find(o => o.value === this.innerValue));
		this.modelChange.emit(this.innerValue);
	}

	// Methods
	// update options to reflect state correctly
	getOptionsUpdated(selectedValue: string): IGenericSelectOption[] {
		return this.options.map(opt => {
			opt.selected = !!(opt.value === selectedValue);
			return opt;
		});
	}

	classGen() {
		return {
			[`select-${this.size}`]: this.size && (this.size === 'large' || this.size === 'small'),
			[`select-width-${this.sizeWidth}`]: this.sizeWidth,
			disabled: this.disabled && this.disabled !== 'false'
		};
	}

	writeValue(value: string) {
		super.writeValue(value);
		this.options = this.getOptionsUpdated(this.innerValue);
	}
}
